import React, { useState, useEffect, useCallback } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from '../../../../components/Providers/FloatMessage';
import { ResetDynamic } from '../../../../utilities/BL/Admin/Overlays/Playlist';
import { Confirm } from '../../../Commons/Modals/Confirm';

/**
 * Modal de Confirmación y Purga Total (Zona de Peligro) para la dinámica "Arma la Playlist" (Tile ID 46).
 * Solicita la contraseña maestra de seguridad y efectúa una confirmación explícita con Confirm.js.
 * 
 * @component PlaylistResetConfirmModal
 * @param {Object} props
 * @param {boolean} props.isOpen - Controla si el modal está visible.
 * @param {Function} props.onClose - Callback al cerrar el modal.
 * @param {Function} [props.onSuccess] - Callback al reiniciar exitosamente.
 */
export const PlaylistResetConfirmModal = ({ isOpen, onClose, onSuccess }) => {
    const { showMessage } = useFloatMessage();

    const [password, setPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    // Resetear contraseña al abrir
    useEffect(() => {
        if (isOpen) {
            setPassword('');
            setIsSubmitting(false);
            setShowConfirmModal(false);
        }
    }, [isOpen]);

    // Cerrar con Escape
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape' && isOpen && !isSubmitting) {
            onClose();
        }
    }, [isOpen, isSubmitting, onClose]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    if (!isOpen) return null;

    const handlePromptConfirm = (e) => {
        e.preventDefault();

        if (!password.trim()) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Debes ingresar la contraseña maestra para continuar.',
                message: 'Debes ingresar la contraseña maestra para continuar.'
            });
            return;
        }

        setShowConfirmModal(true);
    };

    const handleExecuteReset = async () => {
        setIsSubmitting(true);
        try {
            const res = await ResetDynamic(password.trim());

            if (res && res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '✅ Dinámica de Playlist reiniciada y purgada exitosamente.',
                    message: '✅ Dinámica de Playlist reiniciada y purgada exitosamente.'
                });

                if (onSuccess && typeof onSuccess === 'function') {
                    onSuccess();
                }
                onClose();
                return { success: true };
            } else {
                const errMsg = (res && res.message) || 'Contraseña maestra incorrecta. No se realizó el reinicio.';
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: errMsg,
                    message: errMsg
                });
                return { success: false, message: errMsg };
            }
        } catch (error) {
            const errMsg = 'Error de conexión al purgar la dinámica.';
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: errMsg,
                message: errMsg
            });
            return { success: false, message: errMsg };
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div className="PlaylistModal__Overlay" role="dialog" aria-modal="true">
                <div className="PlaylistModal__Backdrop" onClick={() => !isSubmitting && onClose()} />
                <div className="PlaylistModal__Dialog">
                    <div className="PlaylistModal__Content PlaylistModal__Content--danger">
                        {/* Header */}
                        <div className="PlaylistModal__Header">
                            <div className="d-flex align-items-center gap-2">
                                <span className="PlaylistModal__HeaderIcon PlaylistModal__HeaderIcon--danger">
                                    <i className="bi bi-exclamation-octagon-fill text-danger"></i>
                                </span>
                                <div>
                                    <h4 className="PlaylistModal__Title text-danger mb-0">
                                        Zona de Peligro • Purgar Dinámica
                                    </h4>
                                    <p className="PlaylistModal__Subtitle mb-0">
                                        Reinicio total a ceros con validación de seguridad
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="PlaylistModal__CloseButton"
                                onClick={onClose}
                                disabled={isSubmitting}
                                aria-label="Cerrar modal"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>

                        {/* Body */}
                        <form onSubmit={handlePromptConfirm}>
                            <div className="PlaylistModal__Body">
                                <div className="alert alert-danger d-flex align-items-center gap-2 py-2 px-3 mb-3 border-0 bg-danger bg-opacity-10 text-danger rounded-3">
                                    <i className="bi bi-exclamation-triangle-fill fs-4 flex-shrink-0"></i>
                                    <div className="small">
                                        <strong>Acción Irreversible:</strong> Se eliminarán todas las sugerencias de los aficionados, conteos de votos y la lista activa del estadio.
                                    </div>
                                </div>

                                <div className="PlaylistModal__Field mb-1">
                                    <label className="PlaylistModal__Label" htmlFor="reset-modal-password">
                                        Contraseña Maestra / PIN de Seguridad <span className="text-danger">*</span>
                                    </label>
                                    <div className="PlaylistModal__InputWrapper">
                                        <i className="bi bi-shield-lock-fill PlaylistModal__InputIcon text-danger"></i>
                                        <input
                                            id="reset-modal-password"
                                            type="password"
                                            className="PlaylistModal__Input PlaylistModal__Input--danger"
                                            placeholder="Ingresa la contraseña maestra"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            autoFocus
                                        />
                                    </div>
                                    <span className="PlaylistModal__HelpText text-danger">
                                        Requiere clave administrativa autorizada para autorizar la purga.
                                    </span>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="PlaylistModal__Footer">
                                <button
                                    type="button"
                                    className="PlaylistModal__Btn PlaylistModal__Btn--secondary"
                                    onClick={onClose}
                                    disabled={isSubmitting}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="PlaylistModal__Btn PlaylistModal__Btn--danger"
                                    disabled={isSubmitting || !password.trim()}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Validando...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-trash-fill me-2"></i>
                                            Proceder al Reinicio
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Confirm.js Modal de Doble Validación */}
            {showConfirmModal && (
                <Confirm
                    message="¿Confirmas definitivamente que deseas reiniciar y purgar toda la dinámica de Playlist? Todos los votos y sugerencias acumuladas se perderán."
                    textYesButton="Sí, purgar ahora"
                    textNoButton="Cancelar"
                    actionToYesButton={handleExecuteReset}
                    setShowModal={setShowConfirmModal}
                />
            )}
        </>
    );
};

export default PlaylistResetConfirmModal;
